/**
 * Once-per-lifetime funnel event helpers.
 *
 * Each function calls the idempotent backend endpoint to flip the flag, then
 * fires the corresponding Plausible event only if the backend confirms the flag
 * was newly set.  All calls are fire-and-forget; errors are swallowed.
 *
 * Day-1/day-7 detection runs on page load in Layout.tsx — no background
 * scheduler.  Only users registered after FUNNEL_TRACKING_SINCE are eligible
 * (accounts predating the migration have meaningless timing data).
 */

import api from '../api/client'
import { trackEvent } from './analytics'
import type { User } from '../types'

// Users created before this date are excluded from day-1/day-7 detection to
// avoid spurious events from accounts with registration dates too far in the past.
const FUNNEL_TRACKING_SINCE = '2026-05-19'

async function callFunnelEndpoint(event: string): Promise<boolean> {
  try {
    const { data } = await api.post<{ fired: boolean }>('/api/v1/analytics/funnel-event', { event })
    return data.fired
  } catch {
    return false
  }
}

/**
 * Fire `first_card_studied` the first time the user submits a correct answer.
 * Pass setUser so the local state is updated immediately — prevents double-fire
 * within the same session if the page re-renders before the next /me fetch.
 */
export async function fireFirstCardStudied(
  user: User,
  setUser: (u: User) => void,
): Promise<void> {
  if (user.first_card_studied_at !== null) return
  const fired = await callFunnelEndpoint('first_card_studied')
  if (fired) {
    trackEvent('first_card_studied')
    setUser({ ...user, first_card_studied_at: new Date().toISOString() })
  }
}

/**
 * Check and fire day-1 and day-7 return events on authenticated page load.
 * Call this once per user session (from Layout.tsx effect keyed on user.id).
 */
export async function fireDayReturnEvents(
  user: User,
  setUser: (u: User) => void,
): Promise<void> {
  // Gate: skip users created before this migration shipped.
  if (!user.created_at || user.created_at < FUNNEL_TRACKING_SINCE) return

  const createdMs = new Date(user.created_at).getTime()
  const nowMs = Date.now()
  const hoursElapsed = (nowMs - createdMs) / 3_600_000

  if (!user.day_1_return_fired && hoursElapsed >= 20 && hoursElapsed <= 48) {
    const fired = await callFunnelEndpoint('day_1_return')
    if (fired) {
      trackEvent('day_1_return')
      setUser({ ...user, day_1_return_fired: true })
    }
  }

  const daysElapsed = hoursElapsed / 24
  if (!user.day_7_return_fired && daysElapsed >= 6 && daysElapsed <= 14) {
    const fired = await callFunnelEndpoint('day_7_return')
    if (fired) {
      trackEvent('day_7_return')
      setUser({ ...user, day_7_return_fired: true })
    }
  }
}
